import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { plansAPI, progressAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import SubscriptionGate from '../../components/SubscriptionGate';
import { motion } from 'framer-motion';
import { Lock, CheckCircle2, Play, Clock, ChevronRight, AlertCircle } from 'lucide-react';

function getDayFocus(drills) {
  if (!drills || drills.length === 0) return '';
  const cats = [...new Set(drills.map(d => d.category).filter(Boolean))];
  return cats.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(' + ');
}

function getDayTime(drills) {
  if (!drills || drills.length === 0) return 0;
  return drills.reduce((sum, d) => sum + (d.duration_minutes || 0), 0);
}

export default function ProgressView() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [planRes, progRes] = await Promise.all([
          plansAPI.current(),
          progressAPI.weekly(),
        ]);
        setPlan(planRes.data);
        setProgress(progRes.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const completedMap = {};
  progress.forEach(p => {
    const key = `${p.drill_id}-${p.day_number}`;
    completedMap[key] = true;
  });

  // Only count days with drills
  const daysWithDrills = (plan?.days || []).filter(d => (d.drills || []).length > 0);

  let totalDrills = 0;
  let totalCompleted = 0;
  daysWithDrills.forEach(day => {
    const drills = day.drills || [];
    totalDrills += drills.length;
    drills.forEach(d => {
      if (completedMap[`${d.id}-${day.day_number}`]) totalCompleted++;
    });
  });
  const progressPct = totalDrills > 0 ? Math.round((totalCompleted / totalDrills) * 100) : 0;

  // Find the first incomplete, unlocked day with drills
  const firstActionDay = daysWithDrills.find(day => {
    if (!day.unlocked) return false;
    const drills = day.drills || [];
    const done = drills.filter(d => completedMap[`${d.id}-${day.day_number}`]).length;
    return done < drills.length;
  });

  if (loading) {
    return (
      <Layout title="WEEKLY PLAN">
        <div className="flex justify-center py-24"><div className="spinner" /></div>
      </Layout>
    );
  }

  return (
    <Layout title="WEEKLY PLAN">
      <SubscriptionGate feature="progress">
        <div className="px-4 py-6 space-y-4">
          {!plan ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center shadow-sm">
              <p className="text-[#0A2242] text-sm font-medium">Your next week is being built.</p>
              <p className="text-[#94A3B8] text-xs mt-2">Your coach is preparing your training plan. Check back soon!</p>
            </div>
          ) : (
            <>
              {/* Plan Header */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-2 mb-1">
                  {plan.player_week && (
                    <span className="text-[10px] bg-[#2563EB]/10 text-[#2563EB] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                      Week {plan.player_week}
                    </span>
                  )}
                </div>
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight text-[#0A2242]" data-testid="plan-title">
                  {plan.title}
                </h2>
                {plan.description && (
                  <p className="text-sm text-[#64748B] mt-1">{plan.description}</p>
                )}
              </motion.div>

              {/* Week Progress Bar */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-white border border-[#E2E8F0] rounded-xl p-4 shadow-sm"
                data-testid="week-progress-bar"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#64748B] font-semibold">Week Progress</span>
                  <span className="font-['Barlow_Condensed'] text-sm font-bold text-[#0A2242]">{progressPct}%</span>
                </div>
                <div className="w-full bg-[#E2E8F0] rounded-full h-2.5">
                  <div
                    className="h-2.5 rounded-full transition-all duration-700"
                    style={{
                      width: `${progressPct}%`,
                      background: progressPct === 100 ? '#10B981' : '#2563EB',
                    }}
                  />
                </div>
                <p className="text-[10px] text-[#94A3B8] mt-2">
                  {totalCompleted}/{totalDrills} drills completed
                </p>
              </motion.div>

              {/* Start Day CTA */}
              {firstActionDay && (
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  onClick={() => navigate(`/day/${firstActionDay.day_number}?week=${plan.week_number}`)}
                  className="w-full bg-[#0A2242] text-white font-black uppercase tracking-wide rounded-lg px-6 py-4 transition-all hover:bg-[#0E2D5A] flex items-center justify-center gap-2 text-sm shadow-md"
                  data-testid="start-day-cta"
                >
                  {firstActionDay.is_today ? "Start Today's Training" : `Start ${firstActionDay.day_label || `Day ${firstActionDay.day_number}`}`}
                  <ChevronRight size={18} />
                </motion.button>
              )}

              {/* Days — only show days that have drills */}
              <div className="space-y-3">
                {daysWithDrills.map((day, i) => {
                  const isLocked = !day.unlocked;
                  const isToday = day.is_today;
                  const dayDrills = day.drills || [];
                  const dayCompleted = dayDrills.filter(d => completedMap[`${d.id}-${day.day_number}`]).length;
                  const allDone = dayDrills.length > 0 && dayCompleted === dayDrills.length;
                  const focus = getDayFocus(dayDrills);
                  const totalTime = getDayTime(dayDrills);

                  let statusLabel, statusColor, statusBg;
                  if (isLocked) { statusLabel = 'Locked'; statusColor = 'text-[#94A3B8]'; statusBg = 'bg-[#F1F5F9]'; }
                  else if (allDone) { statusLabel = 'Complete'; statusColor = 'text-[#10B981]'; statusBg = 'bg-[#10B981]/10'; }
                  else if (dayCompleted > 0) { statusLabel = 'In Progress'; statusColor = 'text-[#2563EB]'; statusBg = 'bg-[#2563EB]/10'; }
                  else { statusLabel = 'Not Started'; statusColor = 'text-[#94A3B8]'; statusBg = 'bg-[#F1F5F9]'; }

                  return (
                    <motion.button
                      key={day.day_number}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                      onClick={() => !isLocked && navigate(`/day/${day.day_number}?week=${plan.week_number}`)}
                      disabled={isLocked}
                      className={`w-full text-left bg-white border rounded-xl overflow-hidden transition-all shadow-sm ${
                        isToday ? 'border-[#2563EB] shadow-md shadow-[#2563EB]/10' :
                        isLocked ? 'border-[#E2E8F0] opacity-50 cursor-not-allowed' :
                        allDone ? 'border-[#10B981]/30 hover:border-[#10B981]/60' :
                        'border-[#E2E8F0] hover:border-[#2563EB] hover:shadow-md'
                      }`}
                      data-testid={`day-${day.day_number}`}
                    >
                      <div className="p-4">
                        {/* Day header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`font-['Barlow_Condensed'] text-base font-bold ${
                              isToday ? 'text-[#2563EB]' : isLocked ? 'text-[#94A3B8]' : 'text-[#0A2242]'
                            }`}>
                              {day.day_label || `Day ${day.day_number}`}
                            </span>
                            {isToday && (
                              <span className="text-[9px] bg-[#2563EB]/10 text-[#2563EB] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                                Today
                              </span>
                            )}
                          </div>
                          <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${statusBg} ${statusColor}`}>
                            {isLocked ? <span className="flex items-center gap-1"><Lock size={8} /> Locked</span> : statusLabel}
                          </span>
                        </div>

                        {/* Focus area */}
                        {focus && (
                          <p className="text-sm text-[#64748B] font-medium mb-2">{focus}</p>
                        )}

                        {/* Stats row */}
                        <div className="flex items-center gap-4">
                          <span className="text-[10px] text-[#94A3B8] font-bold">
                            {dayDrills.length} drill{dayDrills.length !== 1 ? 's' : ''}
                          </span>
                          {totalTime > 0 && (
                            <span className="flex items-center gap-1 text-[10px] text-[#94A3B8]">
                              <Clock size={10} /> {totalTime} min
                            </span>
                          )}
                          {!isLocked && (
                            <span className="text-[10px] text-[#94A3B8] font-bold">
                              {dayCompleted}/{dayDrills.length} done
                            </span>
                          )}
                        </div>

                        {/* Progress bar */}
                        {!isLocked && (
                          <div className="w-full bg-[#E2E8F0] rounded-full h-1.5 mt-3">
                            <div
                              className="h-1.5 rounded-full transition-all duration-500"
                              style={{
                                width: `${(dayCompleted / dayDrills.length) * 100}%`,
                                background: allDone ? '#10B981' : '#2563EB',
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Chevron */}
                      {!isLocked && (
                        <div className="border-t border-[#E2E8F0] px-4 py-2 flex items-center justify-end">
                          <ChevronRight size={16} className="text-[#CBD5E1]" />
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>

              {/* Next Week Locked */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white border border-[#E2E8F0] rounded-xl p-5 text-center shadow-sm"
                data-testid="next-week-locked"
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Lock size={16} className="text-[#94A3B8]" />
                  <span className="font-['Barlow_Condensed'] text-base font-bold text-[#94A3B8]">
                    Week {(plan.player_week || plan.week_number || 1) + 1}
                  </span>
                </div>
                <p className="text-xs text-[#94A3B8]">
                  Unlocks after completing Week {plan.player_week || plan.week_number || 1}
                </p>
              </motion.div>
            </>
          )}
        </div>
      </SubscriptionGate>
    </Layout>
  );
}
